import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { resolveCrewRolesFromStaging } from "src/app/cbta-web/checklist/ts1-staging-preview.utils";
import { ContainerRatingsService } from "src/app/cbta-web/ratings/container-ratings-form/services/base/container-ratings.services";
import {
	filterTs1Step2SummaryComments,
	isTs1InstructorCommentVisible,
} from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v3/preview/ts1-preview-summary-comments.utils";
import {
	TEM_SAFETY_OUTCOME_LABEL,
	maneuverOutcomeSummaryCommentEditable,
} from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-outcome-interaction";
import { isTs1SummaryObCommentMissing } from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-step3-validation.utils";
import {
	bucketTs1V3SummaryModalCommentsByCandidate,
	formatTs1SummaryCompetencyStatusLabel,
	formatTs1SummaryManeuverLabel,
	getTs1SummaryCandidateSortKey,
	getTs1SummaryCommentDeleteMode,
	isTs1SummaryCommentDeletable,
	scopeTs1V3SummaryModalCommentsForReviewCandidate,
} from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-summary-display.utils";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { DraftItem, OBComments, OBCommentsReq } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { obEventMatchesRole, parseObsCode } from "../../../shared-v2/utils/observation.utils";

type SortColumn = "maneuver" | "competency" | "candidate";
type SortDirection = "asc" | "desc" | null;

interface GroupSortState {
	column: SortColumn | null;
	direction: SortDirection;
}

interface CandidateCommentGroup {
	candidateId: string;
	candidateName: string;
	candidateIndex: number;
	candidateRole: CandidateRole;
	candidateRoleLabel: string;
	comments: OBComments[];
	defaultComments: OBComments[];
	expanded: boolean;
	sort: GroupSortState;
}

@Component({
	selector: "app-ts1-v3-form-summary-modal",
	templateUrl: "./ts1-v3-form-summary-modal.component.html",
	styleUrls: ["./ts1-v3-form-summary-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1V3FormSummaryModalComponent implements OnInit, OnChanges {
	@Input() comments: OBComments[] = [];
	@Input() traineeAssessmentId: string = "";
	/** Step 3 review layout — TEM rows use Rating per Competency / PRO - C format. */
	@Input() reviewLayout = false;
	/** Step 3 carousel candidate — when set with reviewLayout, only that candidate's table is shown. */
	@Input() activeCandidateId = "";

	CANDIDATE_ROLE = CONSTANT.CANDIDATE_ROLE;
	readonly CandidateRole = CandidateRole;
	readonly temSafetyOutcomeLabel = TEM_SAFETY_OUTCOME_LABEL;
	readonly isMissingRequiredComment = isTs1SummaryObCommentMissing;

	candidateGroups: CandidateCommentGroup[] = [];
	editable: Record<string, boolean> = {};
	addObComments = new UntypedFormControl("");
	private isDeleteDialogOpen = false;

	constructor(
		private containerRatingsService: ContainerRatingsService,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.resetEditable();
		this.rebuildCandidateGroups();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["comments"] || changes["reviewLayout"] || changes["activeCandidateId"]) {
			// Clear edit mode whenever the visible comment set can change (candidate/layout switch).
			this.resetEditable();
			this.rebuildCandidateGroups();
		}
	}

	rebuildCandidateGroups(): void {
		this.candidateGroups = this.buildCandidateGroups(this.comments);
	}

	toggleGroupExpanded(group: CandidateCommentGroup): void {
		group.expanded = !group.expanded;
	}

	onSortColumn(group: CandidateCommentGroup, column: SortColumn): void {
		if (group.sort.column === column) {
			if (group.sort.direction === "asc") {
				group.sort.direction = "desc";
			} else {
				group.sort.column = null;
				group.sort.direction = null;
				group.comments = [...group.defaultComments];
				return;
			}
		} else {
			group.sort.column = column;
			group.sort.direction = "asc";
		}

		group.comments = this.sortComments(
			group.defaultComments,
			group.sort.column!,
			group.sort.direction as "asc" | "desc",
		);
	}

	isSortActive(group: CandidateCommentGroup, column: SortColumn): boolean {
		return group.sort.column === column && group.sort.direction != null;
	}

	buildCandidateGroups(comments: OBComments[]): CandidateCommentGroup[] {
		if (this.reviewLayout && this.activeCandidateId) {
			return this.buildReviewLayoutCandidateGroups(comments, this.activeCandidateId);
		}
		return this.buildAllCandidateGroups(comments);
	}

	/** Step 3 review — single accordion for the active carousel candidate (REPEAT stays, sibling tables hidden). */
	private buildReviewLayoutCandidateGroups(comments: OBComments[], activeCandidateId: string): CandidateCommentGroup[] {
		const scoped = scopeTs1V3SummaryModalCommentsForReviewCandidate(comments, activeCandidateId);
		const addRemoveOb = this.containerRatingsService.draft?.candidateRating?.addRemoveOb;
		const groupComments = filterTs1Step2SummaryComments(scoped, addRemoveOb);
		if (groupComments.length === 0) return [];

		const crewRoles = this.resolveCrewRoles();
		const candidateIndex = this.candidateIndexForId(activeCandidateId);
		const candidateRole = crewRoles[candidateIndex] ?? (candidateIndex === 1 ? CandidateRole.PM : CandidateRole.PF);

		return [
			{
				candidateId: activeCandidateId,
				candidateName: this.candidateNameForId(activeCandidateId),
				candidateIndex,
				candidateRole,
				candidateRoleLabel: this.crewRoleLabel(candidateRole),
				comments: [...groupComments],
				defaultComments: [...groupComments],
				expanded: true,
				sort: { column: null, direction: null },
			},
		];
	}

	/** Step 2 (and unscoped) — group all crew candidates; fan REPEAT into both seats. */
	private buildAllCandidateGroups(comments: OBComments[]): CandidateCommentGroup[] {
		const resetManeuvers = this.containerRatingsService.draft?.candidateRating?.resetManeuvers ?? [];
		const crewRoles = this.resolveCrewRoles();
		const crewCandidateIds = (this.containerRatingsService.draft?.trainingInformation?.candidates ?? [])
			.map((c) => c.candidateId)
			.filter((id): id is string => !!id);

		const { order, groupsMap } = bucketTs1V3SummaryModalCommentsByCandidate(comments, {
			resetManeuvers,
			crewCandidateIds,
		});

		return order
			.map((candidateId) => {
				const addRemoveOb = this.containerRatingsService.draft?.candidateRating?.addRemoveOb;
				const rawComments = groupsMap.get(candidateId) ?? [];
				const groupComments = filterTs1Step2SummaryComments(rawComments, addRemoveOb);
				const candidateIndex = this.candidateIndexForId(candidateId);
				const candidateRole = crewRoles[candidateIndex] ?? (candidateIndex === 1 ? CandidateRole.PM : CandidateRole.PF);
				return {
					candidateId,
					candidateName: this.candidateNameForId(candidateId),
					candidateIndex,
					candidateRole,
					candidateRoleLabel: this.crewRoleLabel(candidateRole),
					comments: [...groupComments],
					defaultComments: [...groupComments],
					expanded: true,
					sort: { column: null, direction: null },
				};
			})
			.filter((group) => group.comments.length > 0)
			.sort((a, b) => a.candidateIndex - b.candidateIndex);
	}

	getCompetencySortKey(comment: OBComments): string {
		if (comment.isManeuverOutcome) {
			const rate = comment.rate ?? 0;
			return `maneuver-outcome-${String(rate).padStart(2, "0")}`;
		}
		if (comment.isTemCompetencyOutcome) {
			const rate = comment.rate ?? 0;
			return `tem-${comment.competencyCode ?? ""}-${String(rate).padStart(2, "0")}`;
		}
		if (comment.ob === "REPEAT") return "repeat";

		const ob = comment.ob ?? "";
		const match = ob.match(/^([A-Za-z]+)\s*(\d+)?/);
		if (match) {
			const category = match[1].toLowerCase();
			const num = match[2] ? parseInt(match[2], 10) : 0;
			return `${category}-${String(num).padStart(4, "0")}`;
		}
		return ob.toLowerCase();
	}

	sortComments(comments: OBComments[], column: SortColumn, direction: "asc" | "desc"): OBComments[] {
		const mult = direction === "asc" ? 1 : -1;
		return [...comments].sort((a, b) => {
			const keyA = this.sortKeyForColumn(a, column);
			const keyB = this.sortKeyForColumn(b, column);
			const cmp = keyA.localeCompare(keyB, undefined, { numeric: true }) * mult;
			if (cmp !== 0) return cmp;
			const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
			const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
			return (ta - tb) * mult;
		});
	}

	private sortKeyForColumn(comment: OBComments, column: SortColumn): string {
		if (column === "maneuver") return (comment.maneuver ?? "").toLowerCase();
		if (column === "candidate") {
			return getTs1SummaryCandidateSortKey(comment, this.candidateIndexForId(comment.candidateId));
		}
		return this.getCompetencySortKey(comment);
	}

	commentRowKey(comment: OBComments, index = 0): string {
		if (comment.commentId) {
			return `cid-${comment.commentId}`;
		}
		if (comment.candidateId && comment.maneuverCode) {
			const kind = comment.isManeuverOutcome
				? "mo"
				: comment.isTemCompetencyOutcome
					? "tem"
					: comment.isObservationComment
						? "obs-note"
						: "ob";
			return `${comment.candidateId}::${comment.maneuverCode}::${comment.observationCode ?? comment.ob}::${comment.candidateRole ?? ""}::${comment.createdAt ?? ""}::${kind}`;
		}
		return `idx-${index}`;
	}

	isRowEditable(comment: OBComments, index: number): boolean {
		return !!this.editable[this.commentRowKey(comment, index)];
	}

	hasInstructorComment(comment: OBComments): boolean {
		return isTs1InstructorCommentVisible(
			comment,
			undefined,
			this.containerRatingsService.draft?.candidateRating?.addRemoveOb,
		);
	}

	canEditSummaryComment(comment: OBComments): boolean {
		return maneuverOutcomeSummaryCommentEditable(comment);
	}

	canDeleteSummaryComment(comment: OBComments): boolean {
		return isTs1SummaryCommentDeletable(comment) && this.canEditSummaryComment(comment);
	}

	editComment(comment: OBComments, rowIndex: number): void {
		if (!this.canEditSummaryComment(comment)) return;
		this.resetEditable();
		const key = this.commentRowKey(comment, rowIndex);
		this.editable[key] = true;
		this.addObComments.setValue(comment.instructorComment ?? "");
	}

	deleteComment(comment: OBComments, rowIndex: number): void {
		const mode = getTs1SummaryCommentDeleteMode(comment);
		if (!mode || !this.canDeleteSummaryComment(comment) || this.isDeleteDialogOpen) return;

		this.isDeleteDialogOpen = true;
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: "Are you sure to delete?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		dialogRef.afterClosed().subscribe((result: { close?: boolean } | undefined) => {
			this.isDeleteDialogOpen = false;
			if (result?.close) {
				this.applyDeleteComment(comment, mode);
			}
		});
	}

	private applyDeleteComment(comment: OBComments, mode: "clear" | "remove"): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		if (!record) return;

		const candidateRating = record.candidateRating ?? {};
		let selectedOBs = [...(candidateRating.selectedOBs ?? [])];
		let addRemoveOb = [...(candidateRating.addRemoveOb ?? [])];

		if (mode === "clear") {
			selectedOBs.forEach((item: OBComments) => {
				if (this.matchesSummaryObInSelectedOBs(item, comment)) {
					item.instructorComment = "";
				}
			});
			addRemoveOb.forEach((item: OBCommentsReq) => {
				if (this.matchesSummaryObInAddRemoveOb(item, comment)) {
					item.description = "";
				}
			});
			comment.instructorComment = "";
			for (const row of this.comments) {
				if (this.isSameSummaryRow(row, comment)) {
					row.instructorComment = "";
				}
			}
		} else {
			selectedOBs = selectedOBs.filter((item) => !this.matchesSummaryObInSelectedOBs(item, comment));
			addRemoveOb = addRemoveOb.filter((item) => !this.matchesSummaryObInAddRemoveOb(item, comment));
			for (let i = this.comments.length - 1; i >= 0; i--) {
				if (this.isSameSummaryRow(this.comments[i], comment)) {
					this.comments.splice(i, 1);
				}
			}
		}

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...candidateRating,
				selectedOBs,
				addRemoveOb,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: record.continuePage ?? 2,
		});

		this.resetEditable();
		this.rebuildCandidateGroups();
	}

	saveComment(comment: OBComments, rowIndex: number): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		if (!record) return;

		const candidateRating = record?.candidateRating ?? {};
		const addRemoveOb = candidateRating?.addRemoveOb ?? [];
		const selectedOBs = candidateRating?.selectedOBs ?? [];
		const key = this.commentRowKey(comment, rowIndex);
		const savedText = String(this.addObComments.value ?? "")
			.trim()
			.toUpperCase();

		if (comment?.isManeuverOutcome || comment?.isTemCompetencyOutcome) {
			selectedOBs.forEach((item: OBComments) => {
				if (
					item.candidateId === comment.candidateId &&
					item.ob === comment.ob &&
					item.maneuverCode === comment.maneuverCode &&
					item.competencyCode === comment.competencyCode &&
					item.createdAt === comment.createdAt
				) {
					item.instructorComment = savedText;
				}
			});
		} else if (comment?.ob === "TEM") {
			selectedOBs.forEach((item: OBComments) => {
				if (item.ob === comment.ob && item.candidateId === comment.candidateId) {
					item.instructorComment = savedText;
				}
			});
		} else {
			selectedOBs.forEach((item: OBComments) => {
				if (this.matchesSummaryObInSelectedOBs(item, comment)) {
					item.instructorComment = savedText;
				}
			});
			addRemoveOb.forEach((item: OBCommentsReq) => {
				if (this.matchesSummaryObInAddRemoveOb(item, comment)) {
					item.description = savedText;
				}
			});
		}

		comment.instructorComment = savedText;
		for (const row of this.comments) {
			if (this.isSameSummaryRow(row, comment)) {
				row.instructorComment = savedText;
			}
		}

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...candidateRating,
				selectedOBs,
				addRemoveOb,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: record.continuePage ?? 2,
		});

		this.editable[key] = false;
		this.rebuildCandidateGroups();
	}

	private observationCodeToObLabel(observationCode: string): string {
		const { cat, num } = parseObsCode(observationCode);
		if (!cat) return observationCode;
		return num != null ? `${cat} ${num}` : cat;
	}

	private matchesSummaryObInSelectedOBs(item: OBComments, comment: OBComments): boolean {
		if (item.isManeuverOutcome || item.isTemCompetencyOutcome || item.isReset) return false;
		if (!!item.isObservationComment !== !!comment.isObservationComment) return false;
		if (item.candidateId !== comment.candidateId) return false;
		if (item.maneuverCode !== comment.maneuverCode) return false;
		if (!this.summaryObCodesMatch(item, comment)) return false;
		if (!obEventMatchesRole(item, comment.candidateRole)) return false;
		if (comment.createdAt && item.createdAt) {
			return item.createdAt === comment.createdAt;
		}
		return true;
	}

	private matchesSummaryObInAddRemoveOb(item: OBCommentsReq, comment: OBComments): boolean {
		if (item.isReset) return false;
		if (!!item.isObservationComment !== !!comment.isObservationComment) return false;
		if (item.candidateId !== comment.candidateId) return false;
		if (item.maneuverCode !== comment.maneuverCode) return false;
		if (!this.summaryObCodesMatch(item, comment)) return false;
		if (!obEventMatchesRole({ candidateRole: item.candidateRole } as OBComments, comment.candidateRole)) {
			return false;
		}
		if (comment.createdAt) {
			return item.createdAt === comment.createdAt;
		}
		return true;
	}

	private summaryObCodesMatch(
		item: Pick<OBComments, "ob" | "observationCode"> | Pick<OBCommentsReq, "observationCode">,
		comment: OBComments,
	): boolean {
		const itemOb = "ob" in item ? item.ob : undefined;
		const itemCode = item.observationCode;
		const commentCode = comment.observationCode;
		const commentObLabel = commentCode ? this.observationCodeToObLabel(commentCode) : "";
		const itemObLabel = itemCode ? this.observationCodeToObLabel(itemCode) : "";

		return (
			(!!commentCode && itemCode === commentCode) ||
			(!!comment.ob && itemOb === comment.ob) ||
			(!!comment.ob && itemObLabel === comment.ob) ||
			(!!commentCode && itemOb === commentObLabel)
		);
	}

	private isSameSummaryRow(a: OBComments, b: OBComments): boolean {
		if (a.isManeuverOutcome || a.isTemCompetencyOutcome || b.isManeuverOutcome || b.isTemCompetencyOutcome) {
			return (
				a.candidateId === b.candidateId &&
				a.maneuverCode === b.maneuverCode &&
				a.ob === b.ob &&
				a.competencyCode === b.competencyCode &&
				a.createdAt === b.createdAt
			);
		}
		return (
			a.candidateId === b.candidateId &&
			a.maneuverCode === b.maneuverCode &&
			!!a.isObservationComment === !!b.isObservationComment &&
			this.summaryObCodesMatch(a, b) &&
			obEventMatchesRole(a, b.candidateRole) &&
			(!a.createdAt || !b.createdAt || a.createdAt === b.createdAt)
		);
	}

	resetEditable(): void {
		this.editable = {};
	}

	getOutcomeRateClass(rate: number | undefined): string {
		if (rate === 1) return "text-red";
		if (rate === 2) return "text-yellow";
		if (rate === 3) return "text-yellow-60";
		if (rate === 4) return "text-green";
		if (rate === 5) return "text-green-90";
		return "text-grey-60";
	}

	private candidateNameForId(candidateId: string): string {
		const candidates = this.containerRatingsService.draft?.trainingInformation?.candidates ?? [];
		const match = candidates.find((c) => c.candidateId === candidateId);
		if (match?.name) return match.name;
		return this.candidateSlotLabel(this.candidateIndexForId(candidateId));
	}

	private candidateIndexForId(candidateId: string | undefined): number {
		if (!candidateId) return -1;
		const candidates = this.containerRatingsService.draft?.trainingInformation?.candidates ?? [];
		return candidates.findIndex((c) => c.candidateId === candidateId);
	}

	private resolveCrewRoles(): CandidateRole[] {
		const candidates = this.containerRatingsService.draft?.trainingInformation?.candidates ?? [];
		const crewRoleAssignment = this.containerRatingsService.draft?.candidateRating?.crewRoleAssignment;
		return resolveCrewRolesFromStaging(
			candidates.map((c) => ({ candidateId: c.candidateId ?? "" })),
			crewRoleAssignment,
		);
	}

	private crewRoleLabel(role: CandidateRole | undefined): string {
		if (role === CandidateRole.PF) return "Pilot Flying";
		if (role === CandidateRole.PM) return "Pilot Monitoring";
		return "";
	}

	getCommentCandidateLabel(comment: OBComments): string {
		return this.candidateSlotLabel(this.candidateIndexForId(comment.candidateId));
	}

	getCommentCandidateLabelClass(comment: OBComments): string {
		const idx = this.candidateIndexForId(comment.candidateId);
		if (idx === 0) return "ts1-v3-form-summary-modal__candidate-label--1";
		if (idx === 1) return "ts1-v3-form-summary-modal__candidate-label--2";
		return "";
	}

	maneuverSegmentLabel(comment: OBComments): string {
		if (comment?.isTemCompetencyOutcome) {
			return this.reviewLayout ? formatTs1SummaryManeuverLabel(comment, this.resetManeuvers) : "";
		}
		return formatTs1SummaryManeuverLabel(comment, this.resetManeuvers);
	}

	summaryCompetencyStatusLabel(comment: OBComments): string {
		return formatTs1SummaryCompetencyStatusLabel(comment, this.resetManeuvers);
	}

	private get resetManeuvers() {
		return this.containerRatingsService.draft?.candidateRating?.resetManeuvers ?? [];
	}

	private candidateSlotLabel(index: number): string {
		if (index < 0) return "";
		if (index === 0) return "Candidate 1";
		if (index === 1) return "Candidate 2";
		return `Candidate ${index + 1}`;
	}
}
